import { RoleFunction } from '@eagle/common';
import { Geofence, Thing } from '@eagle/core-data-types';
import {
  Acceleration,
  ActivityAccumulatorSummary,
  BucketedAccumulatorSummary,
  CameraEventTypes,
  CameraStatus,
  CommonTrackingLocationUpdateData,
  DangerousCornering,
  DeviceInstalled,
  DeviceUninstalled,
  DriverBehaviorEventTypes,
  DtcMatchTypes,
  DtcTypes,
  DuressEventTypes,
  EventRecordEventTypes,
  FaultEventTypes,
  FaultUpdate,
  FeatureTypes,
  ImageCaptured,
  InertiaAccelerationData,
  InertiaEventTypes,
  InstallationEventTypes,
  PersonChanged,
  PersonEventTypes,
  Rollover,
  SimpleActivityEventTypes,
  SpeedingFinish,
  SpeedingStart,
  ThingEvent,
  TrackingEventTypes,
  TrackingSummary,
  VehicleSeatEventTypes,
  VideoCaptured,
  VideoStatus
} from '@eagle/data-function-types';
import { Box, Chip, IconButton, Paper, Skeleton, Stack, styled, Tooltip, Typography } from '@ethings/ui-toolkit';
import { Grid, useTheme } from '@mui/material';
import { isObject } from 'lodash';
import React, { ComponentProps, FC, ReactNode, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { T_ONE } from '../../constants';
import { useCustomRoutes, useSmallScreen } from '../../hooks';
import { EventFields } from '../../pages/event-history/history-drawer/history-drawer.types';
import { CacheDataTypes, Undefinable } from '../../types';
import { getFormattedFileName, kphToMph, testid, useHasAuthorization } from '../../util';
import { getEventHint, getEventLabelWithFlag, getEventTranslation, getFeatureLabelWithFlag } from '../../util/events';
import { useBatchFaultDataSafe } from '../entity-journey/batch-fault-context';
import { EventAddress } from '../events/event-address';
import { FetchOne } from '../fetch';
import { useBoolFlag } from '../flags';
import { FormatAddress, FormatTimestamp, FormatTimezone } from '../format';
import { CircleIcon, Icon } from '../icons';
import { DataRow, LinkButton, MediaViewButton } from '../static-data';
import { DataImageModal, DataVideoModal } from '../static-data/data-row-modal';
import { HeaderWithSpaces } from '../text';
import { IconWithTooltip } from '../tooltip';
import { isNewEvent, useDtcFaultDescriptionQuery } from './events-table.queries';
import { CategoryProps, TableEventIcon } from './types';

const NestedRowStack = styled(Stack)(({ theme }) => ({
  '& > div': {
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
}));

const { ROLLOVER, ...inertiaEvents } = InertiaEventTypes;
const { DURESS_BUTTON_ACTIVATED } = DuressEventTypes;

type OtherFeatures = 'abs-activated' | 'vsc-activated' | 'airbag-deployed';

// Temporary definitions until types are added
enum AdasEventTypes {
  PRE_COLLISION_WARNING_OCCURRED = 'pre-collision-warning-occurred',
  LANE_DEPARTURE_OCCURRED = 'lane-departure-occurred',
}

// Temporary  until will be added
interface FaultUpdateWithMatchType extends FaultUpdate {
  dtcMatchType?: DtcMatchTypes;
  dtcBrandCode?: string | null;
}

enum AdditionalDriverBehaviorEventTypes {
  ATTENTION_WARNING_OCCURRED = 'attention-warning-occurred',
  DISTRACTION = 'distraction',
  NOT_WEARING_MASK = 'not-wearing-mask',
  SUNGLASSES_DETECTED = 'sunglasses-detected',
  CAMERA_BLOCKED = 'camera-blocked',
}

interface FeatureExclusions {
  [eventTypeId: string]: string[];
}

interface ExclusionConfig {
  global: string[];
  [feature: string]: FeatureExclusions | string[];
}

interface EventDataRowProps extends ComponentProps<typeof DataRow> {
  feature?: string;
  eventTypeId?: string;
  fieldPath?: string;
}

interface HintedDataRowProps extends ComponentProps<typeof DataRow> {
  feature: string;
  eventTypeId: string;
  fieldPath: string;
}

const EVENT_DATA_EXCLUSIONS: ExclusionConfig = {
  global: [
    'additionalData',
    'speed',
    'locationScores',
    'post',
    'pre',
    'sub',
    'source',
    'triggeredBy',
  ],
  'fault/dtc': {
    'update': ['code', 'dtcMatchType', 'faultCode', 'type', 'failureModeIdentifier', 'sourceAddress', 'dtcBrandVariantId', 'dtcSourceId', 'dtcBrandCode'],
  },
};

const SYSTEM_ADMINISTRATOR_ROLE = [RoleFunction.SYSTEM_ADMINISTRATOR] as const;

export const EventCategories = {
  [EventFields.DRIVER_BEHAVIOR]: Object.values({ ...inertiaEvents, ...DriverBehaviorEventTypes, ...VehicleSeatEventTypes, ...AdditionalDriverBehaviorEventTypes }),
  [EventFields.MEDIA]: Object.values(CameraEventTypes),
  [EventFields.SAFETY_INCIDENT]: Object.values({ ROLLOVER, DURESS_BUTTON_ACTIVATED, ...AdasEventTypes }),
  [EventFields.TRACKING]: Object.values(TrackingEventTypes),
  [EventFields.OTHER]: Object.values({ ...EventRecordEventTypes, ...SimpleActivityEventTypes }),
};

const isEmptyOrNull = (value: unknown): boolean => {
  return value === null || value === undefined || (Array.isArray(value) && value.length === 0);
};

const isPathExcluded = (key: string, feature: string, type: string): boolean => {
  if (EVENT_DATA_EXCLUSIONS.global.includes(key)) {
    return true;
  }

  const featureEntry = EVENT_DATA_EXCLUSIONS[feature];
  if (featureEntry) {
    if (!Array.isArray(featureEntry)) {
      const specificExclusions = featureEntry[type] || [];
      if (specificExclusions.includes(key)) {
        return true;
      }
    } else if (featureEntry.includes(key)) {
      return true;
    }
  }

  return false;
};

const filterSensitiveData = (data: unknown, feature: string, eventTypeId: string): Record<string, unknown> => {
  if (typeof data !== 'object' || data === null) return {};

  const dataObj = data as Record<string, unknown>;

  return Object.fromEntries(
    Object.entries(dataObj).filter(([key]) => !isPathExcluded(key, feature, eventTypeId)),
  );
};

export const EventTableCategory: FC<CategoryProps> = ({ eventTypeId, feature }) => {
  const { t } = useTranslation(['common']);
  const header = Object.entries(EventCategories)
    .filter(([_key, value]: [string, string[]]) => value.includes(eventTypeId))
    .map(([key]) => key)[0];

  if (!header) return <HeaderWithSpaces header={feature} />;
  return <span style={{ textTransform: 'capitalize' }}>{t(`common:component.filter-dropdown.labels.${header}`)}</span>;
};

export const EventTableEventIcon: FC<TableEventIcon> = ({ event, showDateBelowEvent }) => {
  const { eventTypeId, occurred, feature, featureTypeId } = event;
  const flagUseEventV3 = useBoolFlag('portals-translation-retrieval-hook-for-event-and-alert-descriptions-feature');
  const isHintEnabled = useBoolFlag('portals-event-translation-hints-feature');
  const { label, hint, featureInstance, featureLabel } = flagUseEventV3 ? getEventTranslation(feature, eventTypeId) : { label: getEventLabelWithFlag(featureTypeId, eventTypeId, '', flagUseEventV3), hint: getEventHint(feature, eventTypeId), featureInstance: undefined, featureLabel: getFeatureLabelWithFlag(feature, flagUseEventV3) };
  const theme = useTheme();
  const smallScreen = useSmallScreen();
  const iconStyling = { paddingRight: '.5rem', margin: 'auto 0' };

  const EventIcon = (): JSX.Element => {
    switch (eventTypeId) {
      case InertiaEventTypes.ROLLOVER: return <Icon data-testid="alert-icon" icon="AlertCircle" style={{ color: theme.palette.error.main, ...iconStyling }} />;
      case InertiaEventTypes.DANGEROUS_CORNERING: return <Icon data-testid="alert-icon" icon="AlertCircle" style={{ color: theme.palette.error.main, ...iconStyling }} />;
      case DuressEventTypes.DURESS_BUTTON_ACTIVATED: return <Icon icon="AlertCircle" style={{ color: theme.palette.warning.main, ...iconStyling }} />;
      case TrackingEventTypes.POWER_ON: return <Icon data-testid="power-on-icon" icon="On" style={{ color: theme.palette.success.main, ...iconStyling }} />;
      case TrackingEventTypes.POWER_OFF: return <Icon data-testid="power-of-icon" icon="On" style={{ color: theme.palette.grey[500], ...iconStyling }} />;
      case TrackingEventTypes.SPEEDING_START:
      case TrackingEventTypes.SPEEDING_FINISH: return <Icon data-testid="speed-icon" icon="SpeedIcon" style={{ color: theme.palette.error.main, ...iconStyling }} />;
      default: return <CircleIcon data-testid="circle-icon" sx={{ color: theme.palette.grey[500], ...iconStyling }} />;
    }
  };

  const primary = featureTypeId === FeatureTypes.EVENT_RECORD_V0 ? featureLabel : label;
  const secondary = featureTypeId === FeatureTypes.EVENT_RECORD_V0 ? label : featureLabel;

  return (
    <Box display="flex">
      <EventIcon />
      <Stack direction="column">
        <Stack direction="row" spacing={theme.spacing(1)}>
          <Typography data-testid="event-card-event-type" >{primary}</Typography>
          {featureInstance && <Chip label={featureInstance} variant="outlined" size="small" />}
        </Stack>
        <Stack direction="row" spacing={theme.spacing(1)}>
          <Typography data-testid="event-card-event-sub-type" variant="caption" paddingTop={isHintEnabled ? theme.spacing(0.6) : 0}>
            {secondary}
          </Typography>
          {isHintEnabled && hint &&
            <Tooltip data-testid="event-hint" title={hint} arrow>
              <IconButton data-testid="event-hint-button" size="small" aria-label="info">
                <Icon data-testid="info-icon" icon="InfoIcon" fontSize="small" color="action" />
              </IconButton>
            </Tooltip>}
        </Stack>
        {occurred && showDateBelowEvent
          && <Typography data-testid="event-card-date-time" mt={smallScreen ? 'inherit' : '-0.5rem'} variant="caption">
            <FormatTimestamp value={occurred} format="shortWithSeconds" />
          </Typography>
        }
      </Stack>
    </Box>
  );
};

export const useEventLabels = (feature: string, eventTypeId: string, featureTypeId: string): { label: Undefinable<string>; featureLabel: Undefinable<string> } => {
  const flagUseEventV3 = useBoolFlag('portals-translation-retrieval-hook-for-event-and-alert-descriptions-feature');
  const { label, featureLabel } = flagUseEventV3 ? getEventTranslation(feature, eventTypeId) : { label: getEventLabelWithFlag(featureTypeId, eventTypeId, '', flagUseEventV3), featureLabel: getFeatureLabelWithFlag(feature, flagUseEventV3) };

  return { label, featureLabel };
};

export const DataRowTrueOrFalse: FC<{ label: string; flag?: boolean; feature?: string; eventTypeId?: string; fieldPath?: string }> = ({ label, flag, feature, eventTypeId, fieldPath }) => {
  const { t } = useTranslation(['common']);

  if (flag === undefined) return null;

  return (
    <EventDataRow label={label} value={flag ? t('common:common.action.true') : t('common:common.action.false')} feature={feature} eventTypeId={eventTypeId} fieldPath={fieldPath} />
  );
};

const AccelerationDisplay: FC<{ acceleration: Acceleration; feature?: string; eventTypeId?: string; fieldPath?: string }> = ({ acceleration, feature, eventTypeId, fieldPath }) => {
  const { t } = useTranslation(['common']);

  if (!acceleration.axisX && !acceleration.axisY && !acceleration.axisZ) {
    return null;
  }

  return (
    <EventDataRow
      label={t('common:component.events.labels.acceleration')}
      value={
        <>
          {acceleration.axisX !== undefined && <Typography>{t('common:component.events.labels.axis', { axis: t('common:component.events.labels.x-axis'), data: acceleration.axisX })}</Typography>}
          {acceleration.axisY !== undefined && <Typography>{t('common:component.events.labels.axis', { axis: t('common:component.events.labels.y-axis'), data: acceleration.axisY })}</Typography>}
          {acceleration.axisZ !== undefined && <Typography>{t('common:component.events.labels.axis', { axis: t('common:component.events.labels.z-axis'), data: acceleration.axisZ })}</Typography>}
        </>
      }
      feature={feature}
      eventTypeId={eventTypeId}
      fieldPath={fieldPath ?? 'acceleration'}
    />
  );
};

export const DataContainsLocation = (data: CommonTrackingLocationUpdateData, feature: string, eventTypeId: string, featureTypeId: string): JSX.Element => {
  const { t } = useTranslation(['common']);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { location, moving, powerOn, velocity: _velocity, ...rest } = data;
  const { hasAuthorization } = useHasAuthorization();
  const hasSystemAdminPermissions = hasAuthorization(SYSTEM_ADMINISTRATOR_ROLE);
  const shouldFilterSensitiveFields = !hasSystemAdminPermissions && eventTypeId === TrackingEventTypes.LOCATION_UPDATE;
  const filteredRest: unknown = shouldFilterSensitiveFields ? filterSensitiveData(rest as unknown, feature, eventTypeId) : rest;

  return (
    <>
      {filteredRest && <NoEventFormat data={filteredRest} feature={feature} eventTypeId={eventTypeId} featureTypeId={featureTypeId} />}
      {'moving' in data && <DataRowTrueOrFalse label={t('common:component.events.labels.moving')} flag={moving} feature={feature} eventTypeId={eventTypeId} fieldPath="moving" />}
      {'powerOn' in data && <DataRowTrueOrFalse label={t('common:event-descriptions-v3.tracking.power-on.labels')} flag={powerOn} feature={feature} eventTypeId={eventTypeId} fieldPath="powerOn" />}
      {'acceleration' in data && data.acceleration !== '' && <AccelerationDisplay acceleration={data.acceleration as Acceleration} feature={feature} eventTypeId={eventTypeId} fieldPath="acceleration" />}
      {location && (
        <>
          {!isPathExcluded('accuracy', feature, eventTypeId) && location.accuracy !== undefined && (
            <EventDataRow label={t('common:component.events.labels.accuracy')} value={<>{location.accuracy}</>} feature={feature} eventTypeId={eventTypeId} fieldPath="location.accuracy" />
          )}
          {!isPathExcluded('source', feature, eventTypeId) && location.source && (
            <EventDataRow label={t('common:component.events.labels.source')} value={<>{location.source}</>} feature={feature} eventTypeId={eventTypeId} fieldPath="location.source" />
          )}
          {location.altitude && (
            <>
              {!isPathExcluded('altitude', feature, eventTypeId) && location.altitude.value !== undefined && (
                <EventDataRow label={t('common:component.events.labels.altitude')} value={<>{location.altitude.value}</>} feature={feature} eventTypeId={eventTypeId} fieldPath="location.altitude.value" />
              )}
              {!isPathExcluded('altitude-accuracy', feature, eventTypeId) && location.altitude.accuracy !== undefined && (
                <EventDataRow label={t('common:component.events.labels.altitude-accuracy')} value={<>{location.altitude.accuracy}</>} feature={feature} eventTypeId={eventTypeId} fieldPath="location.altitude.accuracy" />
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

const CommonTrackingLocationUpdateFormat: FC<{ data: CommonTrackingLocationUpdateData; feature: string; eventTypeId: string; featureTypeId: string }> = ({ data, feature, eventTypeId, featureTypeId }) => {
  return DataContainsLocation(data, feature, eventTypeId, featureTypeId);
};

const ActivityAccumulatorSummaryFormat: FC<{ data: ActivityAccumulatorSummary; feature: string; eventTypeId: string }> = ({ data, feature, eventTypeId }) => {
  const { t } = useTranslation(['common']);
  return (
    <>
      <EventDataRow label={t('common:component.events.labels.start')} value={<FormatTimestamp format="long" value={data.start} />} feature={feature} eventTypeId={eventTypeId} fieldPath="start" />
      <EventDataRow label={t('common:component.events.labels.finish')} value={<FormatTimestamp format="long" value={data.finish} />} feature={feature} eventTypeId={eventTypeId} fieldPath="finish" />
      {!isPathExcluded('distance', feature, eventTypeId) && data.distance && data.distance !== null && (
        <EventDataRow label={t('common:component.events.labels.distance')} value={<>{data.distance}</>} feature={feature} eventTypeId={eventTypeId} fieldPath="distance" />
      )}
      {!isPathExcluded('duration', feature, eventTypeId) && data.duration && data.duration !== null && (
        <EventDataRow label={t('common:component.events.labels.duration')} value={<>{data.duration}</>} feature={feature} eventTypeId={eventTypeId} fieldPath="duration" />
      )}
      {!isPathExcluded('count', feature, eventTypeId) && data.count && data.count !== null && (
        <EventDataRow label={t('common:component.events.labels.count')} value={<>{data.count}</>} feature={feature} eventTypeId={eventTypeId} fieldPath="count" />
      )}
    </>
  );
};

const BucketedAccumulatorSummaryFormat: FC<{ data: BucketedAccumulatorSummary; feature: string; eventTypeId: string }> = ({ data, feature, eventTypeId }) => {
  const { t } = useTranslation(['common']);
  return (
    <>
      <EventDataRow label={t('common:component.events.labels.start')} value={<FormatTimestamp format="long" value={data.start} />} feature={feature} eventTypeId={eventTypeId} fieldPath="start" />
      <EventDataRow label={t('common:component.events.labels.finish')} value={<FormatTimestamp format="long" value={data.finish} />} feature={feature} eventTypeId={eventTypeId} fieldPath="finish" />

      {Object.entries(data.buckets).map(([bucket, value]) => {
        if (isPathExcluded(bucket, feature, eventTypeId)) return null;
        return (
          <EventDataRow key={bucket} label={t(`common:event-descriptions-v3.${feature}.buckets.${bucket}.labels`)} value={<>
            {'duration' in value && <EventDataRow label={t('common:component.events.labels.duration')} value={<>{value.duration}</>} feature={feature} eventTypeId={eventTypeId} fieldPath={`buckets.${bucket}.duration`} />}
            {'increase' in value && <EventDataRow label={t('common:component.events.labels.increase')} value={<>{value.increase}</>} feature={feature} eventTypeId={eventTypeId} fieldPath={`buckets.${bucket}.increase`} />}
          </>} feature={feature} eventTypeId={eventTypeId} fieldPath={`buckets.${bucket}`} />
        );
      })}
    </>
  );
};

const OtherDriverBehaviorFormat: FC<{ data: CommonTrackingLocationUpdateData & { type: string }; feature: string; eventTypeId: string; featureTypeId: string }> = ({ data, feature, eventTypeId, featureTypeId }) => {
  const { t } = useTranslation(['common']);
  const formattedData = DataContainsLocation(data, feature, eventTypeId, featureTypeId);
  return (
    <>
      {formattedData}
      {!isPathExcluded('type', feature, eventTypeId) && (
        <EventDataRow label={t('common:component.events.labels.data-type')} value={<>{data.type}</>} feature={feature} eventTypeId={eventTypeId} fieldPath="type" />
      )}
    </>
  );
};

const InertiaFormat: FC<{ data: InertiaAccelerationData | DangerousCornering; feature: string; eventTypeId: string }> = ({ data, feature, eventTypeId }) => {
  const { t } = useTranslation(['common']);

  return (
    <>
      {'location' in data && data.location && (
        <>
          {!isPathExcluded('accuracy', feature, eventTypeId) && data.location.accuracy !== null && (
            <EventDataRow label={t('common:component.events.labels.accuracy')} value={<>{data.location.accuracy}</>} feature={feature} eventTypeId={eventTypeId} fieldPath="location.accuracy" />
          )}
          {!isPathExcluded('source', feature, eventTypeId) && (
            <EventDataRow label={t('common:component.events.labels.source')} value={<>{data.location.source}</>} feature={feature} eventTypeId={eventTypeId} fieldPath="location.source" />
          )}
          {data.location.altitude && (
            <>
              {!isPathExcluded('altitude', feature, eventTypeId) && data.location.altitude.value !== undefined && (
                <EventDataRow label={t('common:component.events.labels.altitude')} value={<>{data.location.altitude.value}</>} feature={feature} eventTypeId={eventTypeId} fieldPath="location.altitude.value" />
              )}
              {!isPathExcluded('altitude-accuracy', feature, eventTypeId) && data.location.altitude.accuracy !== undefined && (
                <EventDataRow label={t('common:component.events.labels.altitude-accuracy')} value={<>{data.location.altitude.accuracy}</>} feature={feature} eventTypeId={eventTypeId} fieldPath="location.altitude.accuracy" />
              )}
            </>
          )}
        </>
      )}
      {'acceleration' in data && data.acceleration && <AccelerationDisplay acceleration={data.acceleration} feature={feature} eventTypeId={eventTypeId} fieldPath="acceleration" />}
    </>
  );
};

const RolloverFormat: FC<{ data: Rollover; feature?: string; eventTypeId?: string }> = ({ data, feature, eventTypeId }) => {
  return (
    <>
      {data.acceleration && <AccelerationDisplay acceleration={data.acceleration} feature={feature} eventTypeId={eventTypeId} fieldPath="acceleration" />}
    </>
  );
};

const TrackingSummaryFormat: FC<{ data: TrackingSummary; feature: string; eventTypeId: string }> = ({ data, feature, eventTypeId }) => {
  const { t } = useTranslation(['common']);
  return (
    <>
      <EventDataRow
        label={t('common:event-descriptions-v3.tracking.start.labels')}
        value={<FormatTimestamp value={data.start} />}
        feature={feature}
        eventTypeId={eventTypeId}
        fieldPath="start"
      />
      <EventDataRow
        label={t('common:event-descriptions-v3.tracking.finish.labels')}
        value={<FormatTimestamp value={data.finish} />}
        feature={feature}
        eventTypeId={eventTypeId}
        fieldPath="finish"
      />
      {!isPathExcluded('maxVelocity', feature, eventTypeId) && data.maxVelocity && (
        <>
          <EventDataRow label={t('common:event-descriptions-v3.tracking.maxVelocity.labels')} value={t('common:component.events.labels.speed-value', { kph: data.maxVelocity.speed, mph: kphToMph(data.maxVelocity.speed) })} feature={feature} eventTypeId={eventTypeId} fieldPath="maxVelocity.speed" />
          {data.maxVelocity.heading && <EventDataRow label={t('common:component.events.labels.heading')} value={<>{data.maxVelocity.heading}</>} feature={feature} eventTypeId={eventTypeId} fieldPath="maxVelocity.heading" />}
        </>
      )}
    </>
  );
};

const SpeedingFormat: FC<{ data: SpeedingStart | SpeedingFinish; feature: string; eventTypeId: string }> = ({ data, feature, eventTypeId }) => {
  const { t } = useTranslation(['common']);
  const speed = data.speedingThreshold?.speed || 0;
  return (
    <>
      {!isPathExcluded('speedingThreshold', feature, eventTypeId) && data.speedingThreshold && (
        <EventDataRow
          label={t('common:component.events.labels.speed')}
          value={t('common:component.events.labels.speed-value', { kph: speed, mph: kphToMph(speed) })}
          feature={feature}
          eventTypeId={eventTypeId}
          fieldPath="speedingThreshold.speed"
        />
      )}
    </>
  );
};

const DeviceFormat: FC<{ data: DeviceInstalled | DeviceUninstalled; feature: string; eventTypeId: string }> = ({ data, feature, eventTypeId }) => {
  const { t } = useTranslation(['common']);
  if (isPathExcluded('deviceId', feature, eventTypeId)) return null;
  return (
    <EventDataRow label={t('common:component.events.thing-event.labels.table.device-id')} value={<>{data.deviceId.deviceId}</>} feature={feature} eventTypeId={eventTypeId} fieldPath="deviceId" />
  );
};

const PersonChangedFormat: FC<{ data: PersonChanged; feature: string; eventTypeId: string }> = ({ data, feature, eventTypeId }) => {
  const { t } = useTranslation(['common', 'terms']);
  const { person } = useCustomRoutes();

  if (isPathExcluded('personId', feature, eventTypeId)) return null;

  const link = data.personId ? { display: t('common:common.action.view'), url: `/${person}/${data.personId}` } : undefined;

  return (
    <EventDataRow value={
      <DataRow
        action={<LinkButton link={link} data-testid='action-button-view' />}
        label={t('terms:person', { count: T_ONE })}
        value={<>{data.personId ? data.personId : t('common:common.hint.no-results.person')}</>}
      />
    }
    />
  );
};

const CameraStatusFormat: FC<{ data: CameraStatus | VideoStatus; feature: string; eventTypeId: string }> = ({ data, feature, eventTypeId }) => {
  const { t, i18n } = useTranslation(['common', 'track']);

  if ('statuses' in data) {
    const { statuses } = data;
    return (
      <>
        {statuses.map((status, index) => (
          <React.Fragment key={index}>
            <EventDataRow label={t('common:terms.start')} value={<FormatTimestamp format="long" value={status.start} />} feature={feature} eventTypeId={eventTypeId} fieldPath={`statuses.${index}.start`} />
            <EventDataRow label={t('common:component.events.labels.status')} value={i18n.exists(`track:page.camera-content.camera-status.labels.${status.status}`) ? t(`track:page.camera-content.camera-status.labels.${status.status}`) : status.status} feature={feature} eventTypeId={eventTypeId} fieldPath={`statuses.${index}.status`} />
          </React.Fragment>
        ))}
      </>
    );
  }

  return (
    <EventDataRow label={t('common:component.events.labels.status')} value={i18n.exists(`track:page.camera-content.camera-status.labels.${data.status}`) ? t(`track:page.camera-content.camera-status.labels.${data.status}`) : data.status} feature={feature} eventTypeId={eventTypeId} fieldPath="status" />
  );
};

const ImageCapturedFormat: FC<{ data: ImageCaptured; thingId: string; occurred: Date; feature: string }> = ({ data, thingId, occurred, feature }) => {
  const { t } = useTranslation(['common']);
  const [showModal, setShowModal] = useState(false);
  const handleModalOpen = (): void => setShowModal(true);
  const handleModalClose = (): void => setShowModal(false);

  return (
    <FetchOne
      id={thingId}
      dataType={CacheDataTypes.THING}
      renderFactory={({ display }: Thing) => {
        const url = data.images.original;
        const displayText = getFormattedFileName(display, url as unknown as string, occurred, feature);
        return (
          <>
            <EventDataRow
              value={
                <DataRow
                  action={<MediaViewButton display={t('common:common.action.view')} onClick={handleModalOpen} />}
                  label={t('common:common.labels.images')}
                  value={displayText}
                />
              }
            />
            <DataImageModal fileName={displayText} handleClose={handleModalClose} open={showModal} url={url as unknown as string} />
          </>
        );
      }}
    />
  );
};

const VideoCapturedFormat: FC<{ data: VideoCaptured; thingId: string; occurred: Date; feature: string }> = ({ data, thingId, occurred, feature }) => {
  const { t } = useTranslation(['common']);
  const [showModal, setShowModal] = useState(false);
  const handleModalOpen = (): void => setShowModal(true);
  const handleModalClose = (): void => setShowModal(false);

  return (
    <FetchOne
      id={thingId}
      dataType={CacheDataTypes.THING}
      renderFactory={({ display }: Thing) => {
        const url = data.videos.original[0];
        const displayText = getFormattedFileName(display, url as unknown as string, occurred, feature);
        return (
          <>
            <EventDataRow
              value={
                <DataRow
                  action={<MediaViewButton display={t('common:common.action.view')} onClick={handleModalOpen} />}
                  label={t('common:common.labels.videos')}
                  value={displayText}
                />
              }
            />
            <DataVideoModal fileName={displayText} handleClose={handleModalClose} open={showModal} url={url as unknown as string} />
          </>
        );
      }}
    />
  );
};

const J1939NewFormat: FC<{ data: FaultUpdateWithMatchType; feature: string; eventTypeId: string; featureTypeId: string; thingId: string }> = ({
  data,
  feature,
  eventTypeId,
  featureTypeId,
  thingId,
}) => {
  const { t } = useTranslation(['common']);
  let isJ1939 = data.dtcMatchType === DtcMatchTypes.J1939;

  const dtcBrandCode = data.dtcBrandCode;
  const filteredData = Object.fromEntries(
    Object.entries(data).filter(([key]) => {
      if (isPathExcluded(key, feature, eventTypeId)) return false;
      if ((dtcBrandCode === 'DF-MX' || dtcBrandCode === 'KW-MX') && key === 'severity') return false;
      return true;
    }),
  );
  const batchContext = useBatchFaultDataSafe();
  const brandVariantId = isNewEvent(data) ? data.dtcBrandVariantId : null;
  const shouldWaitForBatch = brandVariantId !== null && batchContext?.isLoading;
  const hasBatchData = brandVariantId !== null && batchContext && brandVariantId in batchContext.faultDescriptions;
  const batchFaultDescription = hasBatchData ? batchContext.faultDescriptions[brandVariantId] : undefined;

  const faultDescriptionQuery = useDtcFaultDescriptionQuery(
    { eventData: data, thingId },
    { enabled: !shouldWaitForBatch && !hasBatchData },
  );

  const faultDescription = hasBatchData ? batchFaultDescription : faultDescriptionQuery.data;
  const isLoading = (batchContext?.isLoading && brandVariantId !== null) || (!hasBatchData && faultDescriptionQuery.isPending);

  if (!isJ1939 && faultDescription && 'matchType' in faultDescription) {
    isJ1939 = faultDescription.matchType === DtcMatchTypes.J1939;
  }

  const description = faultDescription?.description;
  const descriptionValue = description ? (isJ1939 ? `J1939 - ${description}` : description) : null;

  const updatedData = {
    ...filteredData,
  };

  return (
    <>
      {descriptionValue !== null && (
        <EventDataRow
          label={t('common:event-descriptions-v3.fault.dtc.description.labels')}
          value={isLoading ? <Skeleton /> : descriptionValue}
        />
      )}
      <NoEventFormat data={updatedData} feature={feature} eventTypeId={eventTypeId} featureTypeId={featureTypeId} />
    </>
  );
};

const DTC_VALUE_TRANSLATION_KEYS: Record<string, string> = {
  on: 'common:event-descriptions-v3.fault.dtc.values.on',
  off: 'common:event-descriptions-v3.fault.dtc.values.off',
  true: 'common:event-descriptions-v3.fault.dtc.values.true',
  false: 'common:event-descriptions-v3.fault.dtc.values.false',
  available: 'common:event-descriptions-v3.fault.dtc.values.available',
  unavailable: 'common:event-descriptions-v3.fault.dtc.values.unavailable',
};

const NoEventFormat: FC<{ data: unknown; feature: string; eventTypeId: string; featureTypeId: string; pathPrefix?: string }> = ({ data, feature, eventTypeId, featureTypeId, pathPrefix }) => {
  const { t, i18n } = useTranslation(['common']);

  if (typeof data === 'object' && data !== null) {
    const dataObj = data as Record<string, unknown>;
    const buildFieldPath = (prefix: string | undefined, key: string): string => (prefix ? `${prefix}.${key}` : key);

    if ('location' in dataObj) {
      return <CommonTrackingLocationUpdateFormat data={dataObj as CommonTrackingLocationUpdateData} feature={feature} eventTypeId={eventTypeId} featureTypeId={featureTypeId} />;
    }

    if ('acceleration' in dataObj) {
      return <InertiaFormat data={dataObj as InertiaAccelerationData} feature={feature} eventTypeId={eventTypeId} />;
    }

    if ('speedingThreshold' in dataObj) {
      return <SpeedingFormat data={dataObj as SpeedingStart | SpeedingFinish} feature={feature} eventTypeId={eventTypeId} />;
    }

    if ('deviceId' in dataObj && 'feature' in dataObj) {
      return <DeviceFormat data={dataObj as unknown as DeviceInstalled | DeviceUninstalled} feature={feature} eventTypeId={eventTypeId} />;
    }

    if ('personId' in dataObj && 'feature' in dataObj) {
      return <PersonChangedFormat data={dataObj as unknown as PersonChanged} feature={feature} eventTypeId={eventTypeId} />;
    }

    const formatValue = (value: unknown, fieldPath: string): React.ReactNode => {
      if (React.isValidElement(value)) {
        return value;
      }
      if (value instanceof Date) {
        return <FormatTimestamp value={value} format="long" />;
      }
      if (isObject(value)) {
        return <NoEventFormat data={value} feature={feature} eventTypeId={eventTypeId} featureTypeId={featureTypeId} pathPrefix={fieldPath} />;
      }
      if (feature === 'fault/dtc' && (typeof value === 'boolean' || typeof value === 'string')) {
        const translationKey = DTC_VALUE_TRANSLATION_KEYS[String(value)];
        if (translationKey && i18n.exists(translationKey)) {
          return t(translationKey);
        }
      }
      return String(value);
    };

    const formatFeature = (featureId: string): string => {
      if (featureId.includes('/')) {
        return featureId.replace(/\//g, '.');
      }
      return featureId;
    };

    // Fallback to default object rendering
    const entries = Object.entries(dataObj).filter(([key, value]) => {
      if (isEmptyOrNull(value)) return false;
      return !isPathExcluded(key, feature, eventTypeId);
    });

    if (!entries.length) return null;

    const renderDataRow = (key: string, value: unknown): JSX.Element => {
      const fieldPath = buildFieldPath(pathPrefix, key);

      if (formatFeature(feature) === 'tracking' && key === 'geofenceId') {
        return <FetchOne
          id={value}
          dataType={CacheDataTypes.GEOFENCE}
          renderFactory={({ display }: Geofence) => {
            return (
              <EventDataRow
                key={key}
                label={t('common:common.labels.display')}
                value={display}
                data-testid={testid`data-row-${key}`}
                feature={feature}
                eventTypeId={eventTypeId}
                fieldPath={fieldPath}
              />
            );
          }}
        />;
      }

      return <EventDataRow
        key={key}
        label={t(`common:event-descriptions-v3.${formatFeature(feature)}.${key}.labels`)}
        value={formatValue(value, fieldPath)}
        data-testid={testid`data-row-${key}`}
        feature={feature}
        eventTypeId={eventTypeId}
        fieldPath={fieldPath}
      />;
    };

    return (
      <>
        {entries.map(([key, value]) => renderDataRow(key, value))}
      </>
    );
  }

  return (
    <NestedRowStack m={1} width="100%">
      <Typography variant="body2" fontStyle="italic" color="text.secondary" px={2.5} py={4}>
        {t('common:component.events.thing-event.labels.table.no-formatting-available')}
      </Typography>
      <DataRow
        label={t('common:component.events.thing-event.labels.table.raw-data')}
        value={<pre>{JSON.stringify(data, null, 4)}</pre>}
      />
    </NestedRowStack>
  );
};

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export function EntryDataBody<T extends Omit<ThingEvent, 'schema'>>({ event }: { event: T }): JSX.Element {
  const { t } = useTranslation(['common']);

  if (!event.data) {
    return <>{t('common:component.events.thing-event.labels.table.filter-no-results')}</>;
  }

  return <EntryDataContent event={event} />;
}

const EventDataSection: React.FC<{ event: Omit<ThingEvent, 'schema'> }> = ({ event }) => {
  switch (event.featureTypeId as FeatureTypes & OtherFeatures) {
    case FeatureTypes.ACTIVITY_ACCUMULATOR_V0:
      switch (event.eventTypeId) {
        case 'summary':
          return <ActivityAccumulatorSummaryFormat data={event.data as ActivityAccumulatorSummary} feature={event.feature} eventTypeId={event.eventTypeId} />;
        default:
          return <NoEventFormat data={event.data} feature={event.feature} eventTypeId={event.eventTypeId} featureTypeId={event.featureTypeId} />;
      }
    case FeatureTypes.BUCKETED_ACCUMULATOR_V0:
      switch (event.eventTypeId) {
        case 'summary':
          return <BucketedAccumulatorSummaryFormat data={event.data as BucketedAccumulatorSummary} feature={event.feature} eventTypeId={event.eventTypeId} />;
        default:
          return <NoEventFormat data={event.data} feature={event.feature} eventTypeId={event.eventTypeId} featureTypeId={event.featureTypeId} />;
      }
    case FeatureTypes.CAMERA_V0:
      switch (event.eventTypeId) {
        case CameraEventTypes.CAMERA_STATUS:
        case CameraEventTypes.VIDEO_STATUS:
          return <CameraStatusFormat data={event.data as CameraStatus | VideoStatus} feature={event.feature} eventTypeId={event.eventTypeId} />;
        case CameraEventTypes.IMAGE_CAPTURED:
          return <ImageCapturedFormat data={event.data as ImageCaptured} thingId={event.thingId} occurred={event.occurred} feature={event.feature} />;
        case CameraEventTypes.VIDEO_CAPTURED:
          return <VideoCapturedFormat data={event.data as VideoCaptured} thingId={event.thingId} occurred={event.occurred} feature={event.feature} />;
        default:
          return <NoEventFormat data={event.data} feature={event.feature} eventTypeId={event.eventTypeId} featureTypeId={event.featureTypeId} />;
      }
    case FeatureTypes.DRIVER_BEHAVIOR_V0:
      switch (event.eventTypeId) {
        case DriverBehaviorEventTypes.EYES_CLOSED:
        case DriverBehaviorEventTypes.FATIGUE_RISK_DRIVING:
        case DriverBehaviorEventTypes.LOOK_DOWN:
        case DriverBehaviorEventTypes.MISSING_FACE:
        case DriverBehaviorEventTypes.PHONE_USAGE:
        case DriverBehaviorEventTypes.SMOKING:
        case DriverBehaviorEventTypes.YAWN:
          return DataContainsLocation(event.data as CommonTrackingLocationUpdateData, event.feature, event.eventTypeId, event.featureTypeId);
        case DriverBehaviorEventTypes.OTHER:
          return <OtherDriverBehaviorFormat data={event.data as CommonTrackingLocationUpdateData & { type: string }} feature={event.feature} eventTypeId={event.eventTypeId} featureTypeId={event.featureTypeId} />;
        default:
          return <NoEventFormat data={event.data} feature={event.feature} eventTypeId={event.eventTypeId} featureTypeId={event.featureTypeId} />;
      }
    case FeatureTypes.EVENT_RECORD_V0:
      switch (event.eventTypeId) {
        case EventRecordEventTypes.OCCURRENCE:
          return DataContainsLocation(event.data as CommonTrackingLocationUpdateData, event.feature, event.eventTypeId, event.featureTypeId);
        default:
          return <NoEventFormat data={event.data} feature={event.feature} eventTypeId={event.eventTypeId} featureTypeId={event.featureTypeId} />;
      }
    case FeatureTypes.INERTIA_V0:
      switch (event.eventTypeId) {
        case InertiaEventTypes.DANGEROUS_CORNERING:
        case InertiaEventTypes.RAPID_ACCELERATION:
        case InertiaEventTypes.RAPID_DECELERATION:
          return <InertiaFormat data={event.data} feature={event.feature} eventTypeId={event.eventTypeId} />;
        case InertiaEventTypes.ROLLOVER:
          return <RolloverFormat data={event.data as Rollover} feature={event.feature} eventTypeId={event.eventTypeId} />;
        default:
          return <NoEventFormat data={event.data} feature={event.feature} eventTypeId={event.eventTypeId} featureTypeId={event.featureTypeId} />;
      }
    case FeatureTypes.INSTALLATION_V0:
      switch (event.eventTypeId) {
        case InstallationEventTypes.DEVICE_INSTALLED:
        case InstallationEventTypes.DEVICE_UNINSTALLED:
          return <DeviceFormat data={event.data as DeviceInstalled | DeviceUninstalled} feature={event.feature} eventTypeId={event.eventTypeId} />;
        default:
          return <NoEventFormat data={event.data} feature={event.feature} eventTypeId={event.eventTypeId} featureTypeId={event.featureTypeId} />;
      }
    case FeatureTypes.PERSON_V0:
      switch (event.eventTypeId) {
        case PersonEventTypes.PERSON_CHANGED:
          return <PersonChangedFormat data={event.data as PersonChanged} feature={event.feature} eventTypeId={event.eventTypeId} />;
        default:
          return <NoEventFormat data={event.data} feature={event.feature} eventTypeId={event.eventTypeId} featureTypeId={event.featureTypeId} />;
      }
    case FeatureTypes.SIMPLE_ACTIVITY_V0:
      switch (event.eventTypeId) {
        case SimpleActivityEventTypes.START:
        case SimpleActivityEventTypes.FINISH:
          return DataContainsLocation(event.data as CommonTrackingLocationUpdateData, event.feature, event.eventTypeId, event.featureTypeId);
        default:
          return <NoEventFormat data={event.data} feature={event.feature} eventTypeId={event.eventTypeId} featureTypeId={event.featureTypeId} />;
      }
    case FeatureTypes.TRACKING_V0:
      switch (event.eventTypeId) {
        case TrackingEventTypes.LOCATION_UPDATE:
        case TrackingEventTypes.MOTION_START:
        case TrackingEventTypes.MOTION_FINISH:
        case TrackingEventTypes.POWER_ON:
        case TrackingEventTypes.POWER_OFF:
          return DataContainsLocation(event.data as CommonTrackingLocationUpdateData, event.feature, event.eventTypeId, event.featureTypeId);
        case TrackingEventTypes.SPEEDING_START:
        case TrackingEventTypes.SPEEDING_FINISH:
          return <SpeedingFormat data={event.data as SpeedingStart | SpeedingFinish} feature={event.feature} eventTypeId={event.eventTypeId} />;
        case TrackingEventTypes.SUMMARY:
          return <TrackingSummaryFormat data={event.data as TrackingSummary} feature={event.feature} eventTypeId={event.eventTypeId} />;
        default:
          return <NoEventFormat data={event.data} feature={event.feature} eventTypeId={event.eventTypeId} featureTypeId={event.featureTypeId} />;
      }
    case FeatureTypes.VEHICLE_SEAT_V0:
      switch (event.eventTypeId) {
        case VehicleSeatEventTypes.MOVEMENT_WITHOUT_RESTRAINT_START:
        case VehicleSeatEventTypes.MOVEMENT_WITHOUT_RESTRAINT_FINISH:
          return DataContainsLocation(event.data as CommonTrackingLocationUpdateData, event.feature, event.eventTypeId, event.featureTypeId);
        default:
          return <NoEventFormat data={event.data} feature={event.feature} eventTypeId={event.eventTypeId} featureTypeId={event.featureTypeId} />;
      }
    case FeatureTypes.FAULT_V0:
      switch (event.eventTypeId) {
        case FaultEventTypes.UPDATE: {
          const data = event.data as FaultUpdate;
          if (event.feature === 'fault/dtc' && data.type !== DtcTypes.HINO) {
            return <J1939NewFormat data={data} feature={event.feature} eventTypeId={event.eventTypeId} featureTypeId={event.featureTypeId} thingId={event.thingId} />;
          }
          return <NoEventFormat data={event.data} feature={event.feature} eventTypeId={event.eventTypeId} featureTypeId={event.featureTypeId} />;
        }
        default:
          return <NoEventFormat data={event.data} feature={event.feature} eventTypeId={event.eventTypeId} featureTypeId={event.featureTypeId} />;
      }
    default:
      return <NoEventFormat data={event.data} feature={event.feature} eventTypeId={event.eventTypeId} featureTypeId={event.featureTypeId} />;
  }
};

export const EntryDataContent: FC<{ event: Omit<ThingEvent, 'schema'> }> = ({ event }) => {
  const { t } = useTranslation(['common', 'track']);
  const data = (event.data || {}) as CommonTrackingLocationUpdateData & Record<string, unknown>;
  const hasLocationData = !!(
    data.location?.address ||
    data.location?.latitude !== null && data.location?.latitude !== undefined ||
    data.location?.longitude !== null && data.location?.longitude !== undefined
  );
  const hasVelocityData = !!(data.velocity?.speed || data.velocity?.heading);
  const hasExtraData = !!(data && typeof data === 'object' && Object.entries(data).some(([key, value]) => {
    if (key === 'velocity') return false;
    if (key === 'location' && isObject(value)) {
      return Object.keys(value).some((subKey) =>
        !['address', 'latitude', 'longitude', 'timeZone'].includes(subKey) &&
        !isPathExcluded(subKey, event.feature, event.eventTypeId),
      );
    }
    return !isPathExcluded(key, event.feature, event.eventTypeId);
  }));

  return (
    <Box padding={1}>
      <Grid container spacing={2} data-testid='location-velocity-container'>
        <Grid item xs={12} lg={6}>
          <Stack flexDirection="column" height={1} data-testid='location-section'>
            <Typography variant="body1" pt={.5} pb={1}>{t('track:page.history.event-dialog.location.labels')}</Typography>
            <StyledPaper elevation={0}>
              {hasLocationData ? (
                <Stack alignItems="center" justifyContent="space-between" flexGrow={1}>
                  <Stack flexDirection="row" width="100%" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary" display="block" data-testid='row-address-label'>{t('common:component.events.labels.address')}</Typography>
                    <Box textAlign="right" width={.8} data-testid='row-address-value' >
                      {data.location?.address ? (
                        <FormatAddress
                          value={data.location?.address}
                          addressUnavailableText={t('common:page.thing-map.address-unavailable.hint')}
                          typographyProps={{ lineHeight: '24px' }}
                        />
                      ) : (
                        <EventAddress location={data.location} addressUnavailableText={t('common:page.thing-map.address-unavailable.hint')} />
                      )}
                    </Box>
                  </Stack>
                  <Stack alignItems="center" width="100%">
                    <DataField label={t('common:component.events.labels.latitude')} value={data.location?.latitude} data-testid="data-row-latitude" />
                    <DataField label={t('common:component.events.labels.longitude')} value={data.location?.longitude} data-testid="data-row-longitude" />
                    <DataField label={t('common:component.events.labels.source')} value={data.location?.source} data-testid="data-row-source" />
                  </Stack>
                </Stack>
              ) : (
                <Typography variant="caption" fontStyle="italic" color="text.secondary" px={1} py={1}>
                  {t('track:page.history.event-dialog.no-data-available.labels')}
                </Typography>
              )}
            </StyledPaper>
          </Stack>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Box data-testid='date-time-section'>
            <Typography variant="body1" pt={.5} pb={1}>{t('track:page.history.event-dialog.date-time.labels')}</Typography>
            <StyledPaper elevation={0}>
              <DataField label={t('common:component.map.labels.occurred-at')} value={<FormatTimestamp value={event.occurred} />} data-testid="data-row-occurred-at" />
              <DataField label={t('common:component.events.labels.timezone')} value={<FormatTimezone zone={data.location?.timeZone?.id} />} data-testid="data-row-timezone" />
            </StyledPaper>
          </Box>
          <Box data-testid='velocity-section'>
            <Typography variant="body1" py={1}>{t('common:component.events.labels.velocity')}</Typography>
            <StyledPaper elevation={0}>
              {hasVelocityData ? (
                <>
                  <DataField
                    label={t('common:component.events.labels.speed')}
                    value={t('common:component.events.labels.speed-value', { kph: data.velocity?.speed, mph: kphToMph(data.velocity?.speed as number) || 0 })}
                    data-testid="data-row-speed"
                  />
                  <DataField label={t('common:component.events.labels.heading')} value={data.velocity?.heading} data-testid="data-row-heading" />
                </>
              ) : (
                <Typography variant="caption" fontStyle="italic" color="text.secondary" px={1} py={1}>
                  {t('track:page.history.event-dialog.no-data-available.labels')}
                </Typography>
              )}
            </StyledPaper>
          </Box>
        </Grid>
      </Grid>
      <Box marginTop={2} data-testid='event-data-section'>
        <Typography variant="body1" pb={1}>{t('track:page.history.event-dialog.event-data.labels')}</Typography>
        {hasExtraData ?
          <Grid container spacing={2}>
            <EventDataSection event={event} />
          </Grid>
          :
          <StyledPaper elevation={0}>
            <Typography variant="caption" fontStyle="italic" color="text.secondary" px={1} py={1}>
              {t('track:page.history.event-dialog.no-data-available.labels')}
            </Typography>
          </StyledPaper>
        }
      </Box>
    </Box>
  );
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'wrap',
  flexGrow: 1,
  overflowX: 'hidden',
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
}));

const DataField = ({ label, value, 'data-testid': dataTestId }: { label: string; value: ReactNode; 'data-testid'?: string }): JSX.Element => (
  <Stack
    alignItems="center"
    flexDirection="row"
    width="100%"
    justifyContent="space-between"
    flexWrap="wrap"
    data-testid={dataTestId}
  >
    <Typography variant="caption" color="text.secondary" display="block" data-testid='row-label'>{label}</Typography>
    <Typography textAlign="right" data-testid='row-value'>{value || '-'}</Typography>
  </Stack>
);

const EventDataRow: FC<EventDataRowProps> = ({ feature, eventTypeId, fieldPath, ...rest }) => {
  if (!feature || !eventTypeId || !fieldPath) {
    return (
      <Grid item xs={12} sm={6}>
        <DataRow {...rest} />
      </Grid>
    );
  }

  return (
    <Grid item xs={12} sm={6}>
      <HintedDataRow feature={feature} eventTypeId={eventTypeId} fieldPath={fieldPath} {...rest} />
    </Grid>
  );
};

const buildEventDataHintKey = (feature: string, eventTypeId: string, fieldPath: string): string => {
  const normalizedFieldPath = fieldPath.replace(/\./g, '__');
  return `track:event-data-descriptions.${feature}.${eventTypeId}.data.${normalizedFieldPath}.hint`;
};

const HintedDataRow: FC<HintedDataRowProps> = ({ feature, eventTypeId, fieldPath, ...rest }) => {
  const { t, i18n } = useTranslation(['track']);
  const hintKey = buildEventDataHintKey(feature, eventTypeId, fieldPath);
  const hint = hintKey && i18n.exists(hintKey) ? t(hintKey) : undefined;
  const toolTip = hint ? <IconWithTooltip arrow placement="top" data-testid="event-data-tooltip" icon={<Icon icon="Help" />} title={hint} /> : undefined;
  return <DataRow {...rest} action={toolTip} />;
};
